/**
 * EventPhotoGallery — view and manage photos for a completed event.
 *
 * Photos are stored in IndexedDB keyed by suggestion ID. This
 * component shows a horizontal scrollable strip of thumbnails with
 * an "Add photo" button. Tapping a thumbnail opens it larger;
 * long-press or secondary action removes it.
 *
 * Same privacy model as child photos: these never touch the server.
 * The caption says so explicitly.
 */

import { useRef, useState, type ChangeEvent } from "react";

import { useEventPhotos } from "@/lib/useEventPhotos";

interface EventPhotoGalleryProps {
  suggestionId: string;
}

export function EventPhotoGallery({ suggestionId }: EventPhotoGalleryProps) {
  const { photos, addPhoto, removePhoto } = useEventPhotos(suggestionId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [viewingUrl, setViewingUrl] = useState<string | null>(null);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files === null) return;
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      if (file !== undefined) {
        void addPhoto(file);
      }
    }
    event.target.value = "";
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-3 overflow-x-auto pb-1">
        {photos.map((photo) => (
          <div key={photo.key} className="relative shrink-0 group">
            <button
              type="button"
              onClick={() => setViewingUrl(photo.url)}
              className="w-20 h-20 md:w-24 md:h-24 rounded-md overflow-hidden border border-edge focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
            >
              <img
                src={photo.url}
                alt=""
                className="w-full h-full object-cover"
              />
            </button>
            <button
              type="button"
              onClick={() => removePhoto(photo.key)}
              className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-coral-deep text-white text-[12px] font-bold flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
              aria-label="Remove photo"
            >
              ×
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="w-20 h-20 md:w-24 md:h-24 shrink-0 rounded-md border-2 border-dashed border-edge hover:border-coral-deep flex flex-col items-center justify-center gap-1 text-ink-muted hover:text-coral-deep transition-colors"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
          <span className="text-[10px] font-medium">Add</span>
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="sr-only"
          onChange={handleFileChange}
        />
      </div>

      {photos.length > 0 && (
        <p className="font-sans text-[11px] italic text-ink-dim">
          {photos.length} photo{photos.length !== 1 ? "s" : ""} · stored on this device only
        </p>
      )}

      {viewingUrl !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink/80 px-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setViewingUrl(null)}
        >
          <img
            src={viewingUrl}
            alt=""
            className="max-w-full max-h-[85vh] rounded-lg shadow-xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            onClick={() => setViewingUrl(null)}
            className="absolute top-6 right-6 w-10 h-10 rounded-full bg-surface/90 text-ink flex items-center justify-center text-[18px] font-bold"
            aria-label="Close"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}
